import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <main className="max-w-4xl mx-auto px-6 py-8">
      <div className="bg-white rounded-lg shadow-sm border p-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📜 Privacy Policy</h1>
          <p className="text-gray-600">
            <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-8">
          <p className="text-gray-700 leading-relaxed">
            BuyNothings ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. 
            This Privacy Policy explains what information we collect, how we use it, who we may share it with, and your rights 
            regarding that information when you use our mobile application and services ("App").
          </p>
        </div>

        {/* Section 1 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
          <p className="text-gray-700 mb-4">
            We collect the following types of data to operate and improve our services:
          </p>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">a. Personal Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Approximate or precise location (depending on your settings)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">b. Authentication Information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Sign-up or login method (Email, Google, or Facebook)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">c. User-Generated Content</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Photos of items uploaded to the app</li>
                <li>Chat messages and communications with other users (analyzed to improve experience and enforce community safety)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">d. Device and Usage Data</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Device ID, IP address, operating system version</li>
                <li>App usage statistics, crash reports, and diagnostic logs</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
          <p className="text-gray-700 mb-4">We use the information we collect to:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Enable you to list and request items</li>
            <li>Match users based on location and preferences</li>
            <li>Analyze chat interactions to enhance safety and experience</li>
            <li>Improve app performance and user experience</li>
            <li>Offer customer support and resolve issues</li>
            <li>Personalize content and recommendations</li>
            <li>Provide targeted in-app advertisements</li>
            <li>Generate anonymized data for usage analytics and business insights</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Location Data</h2>
          <p className="text-gray-700 leading-relaxed">
            Your location helps us show relevant listings and connect you with users nearby. We may collect this information 
            while the app is in use or in the background, depending on your device settings and permissions. You can change 
            your location preferences at any time through your device settings.
          </p>
        </section>

        {/* Section 4 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Sharing and Disclosure of Information</h2>
          <p className="text-gray-700 mb-4">
            We do not sell or rent your personal data. We may share your information with trusted service providers who help us run our platform. These include:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
            <li>Analytics partners (to understand app usage and performance)</li>
            <li>Marketing platforms (to show relevant offers and ads)</li>
            <li>Crash and error monitoring tools</li>
            <li>Cloud hosting providers</li>
          </ul>
          <p className="text-gray-700 mb-4">
            All third parties are contractually obligated to use your data only for the services we specify and to protect your information with industry-standard security practices.
          </p>
          <p className="text-gray-700">
            We may also share anonymized or aggregated data for research, reporting, or commercial use.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Your Rights and Choices</h2>
          <p className="text-gray-700 mb-4">You have the right to:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
            <li>Access a copy of your personal information</li>
            <li>Request deletion of your account and associated data</li>
            <li>Correct inaccuracies in your profile</li>
            <li>Withdraw consent for certain data uses (e.g., location sharing, marketing)</li>
          </ul>
          <p className="text-gray-700">
            To exercise any of these rights, please contact us at <a href="mailto:support@buynothings.com" className="text-blue-600 hover:text-blue-800 underline">support@buynothings.com</a>.
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Children's Privacy</h2>
          <p className="text-gray-700 mb-4">
            BuyNothings is not intended for users under the age of 13. If you are between 13 and 17, you may only use the app with parental or guardian consent.
          </p>
          <p className="text-gray-700 mb-4">
            We do not knowingly collect personal information from users under 13. If we learn that we've collected such information without parental verification, we will delete it immediately.
          </p>
          <p className="text-gray-700">
            If you're unsure whether to allow minors, we recommend limiting usage to users 18+ for legal simplicity and compliance with third-party providers like Facebook or Google.
          </p>
        </section>

        {/* Section 7 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Security</h2>
          <p className="text-gray-700 mb-4">We implement a variety of technical and organizational measures to protect your data, including:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
            <li>Encrypted storage of login credentials</li>
            <li>Secure transmission using HTTPS</li>
            <li>Access control and internal audits of our infrastructure</li>
          </ul>
          <p className="text-gray-700">
            Despite our efforts, no system is completely secure. You acknowledge that you use the app at your own risk.
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Policy Updates</h2>
          <p className="text-gray-700 mb-4">We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we do, we will:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
            <li>Notify you through in-app messages and email</li>
            <li>Indicate the "Effective Date" at the top of this page</li>
          </ul>
          <p className="text-gray-700">
            We encourage you to review this policy periodically.
          </p>
        </section>

        {/* Contact Information */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Us</h3>
          <p className="text-gray-700 mb-2">
            If you have any questions about this Privacy Policy, please contact us:
          </p>
          <div className="space-y-2">
            <p className="text-gray-700">
              <strong>Email:</strong> <a href="mailto:support@buynothings.com" className="text-blue-600 hover:text-blue-800 underline">support@buynothings.com</a>
            </p>
            <p className="text-gray-700">
              <strong>Website:</strong> <a href="https://buynothings.com" className="text-blue-600 hover:text-blue-800 underline">https://buynothings.com</a>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
